import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import {
  cameraPosition,
  color,
  deltaTime,
  float,
  Fn,
  hash,
  If,
  instancedArray,
  instanceIndex,
  length,
  positionLocal,
  positionWorld,
  smoothstep,
  uniform,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import Grid from "./Grid"

type MovementDirection =
  | "left-to-right"
  | "right-to-left"
  | "far-to-near"
  | "near-to-far"

export default class GridParticles {
  name: string
  experience: Experience
  renderer: Experience["renderer"]["instance"]
  scene: THREE.Scene
  debugFolder?: FolderApi
  grid: Grid

  positions: ReturnType<typeof instancedArray>
  speeds: ReturnType<typeof instancedArray>
  mesh?: THREE.InstancedMesh
  material!: THREE.NodeMaterial
  computeParticles!: THREE.ComputeNode
  computeInit!: THREE.ComputeNode
  isStarted!: ReturnType<typeof uniform<number>>

  settings = {
    count: 30,
    speed: uniform(1),
    color: uniform(color("#7fb0c8")),
    emissiveIntensity: uniform(0.5),
    size: uniform(0.01),
    direction: "left-to-right" as MovementDirection,
    position: {
      x: undefined as number | undefined,
      y: 5,
      z: undefined as number | undefined,
    },
    minDistance: uniform(1), // Starts fading from this distance
    maxDistance: uniform(8), // Finishes fading at this distance
    hideUntilDistance: uniform(0.5), // Hides particles until this distance
  }

  constructor({
    scene,
    name,
    grid,
    debugFolder,
    count,
    direction,
    position,
    speed,
  }: {
    scene: THREE.Scene
    name: string
    grid: Grid
    debugFolder?: FolderApi
    count: number
    direction: MovementDirection
    position: { x?: number; y: number; z?: number }
    speed: number
  }) {
    this.experience = Experience.getInstance()
    this.renderer = this.experience.renderer.instance
    this.scene = scene
    this.debugFolder = debugFolder
    this.grid = grid
    this.name = name

    this.isStarted = uniform(0)

    this.settings.count = count
    this.settings.direction = direction
    this.settings.position = {
      x: position.x,
      y: position.y,
      z: position.z,
    }
    this.settings.speed.value = speed

    this.positions = instancedArray(this.settings.count, "vec3")
    this.speeds = instancedArray(this.settings.count, "float")

    this.createMeshes()
    this.init()
    this.setDebug()

    this.setEventListeners()
  }

  setEventListeners() {
    this.experience.on(EVENTS.Guestbook.BeforeEnter, () => {
      this.isStarted.value = 0
      // Reset particles to initial position
      this.renderer.computeAsync(this.computeInit)
    })

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.onEnterAnimation.bind(this),
    )
  }

  onEnterAnimation(event: unknown) {
    const { progress } = event as { progress: number }
    if (progress === 1 && this.isStarted.value < 1) {
      this.isStarted.value = 1
    }
  }

  createMeshes() {
    const geometry = new THREE.PlaneGeometry(
      this.settings.size.value,
      this.settings.size.value,
      1,
      1,
    )
    this.material = new THREE.NodeMaterial()
    this.material.transparent = true
    this.material.side = THREE.DoubleSide

    this.material.colorNode = this.settings.color
    ;(this.material as unknown as THREE.MeshPhysicalNodeMaterial).emissiveNode =
      this.settings.color.mul(this.settings.emissiveIntensity)

    this.material.positionNode = Fn(() => {
      const localPos = positionLocal
      const instancePos = this.positions.toAttribute()
      return localPos.add(instancePos)
    })()

    this.material.opacityNode = Fn(() => {
      const distanceFromCamera = length(positionWorld.sub(cameraPosition))

      // farOpacity: fades out as distance increases
      // When distance <= minDistance: fully visible (opacity = 1)
      // When distance >= maxDistance: invisible (opacity = 0)
      const farOpacity = smoothstep(
        this.settings.minDistance,
        this.settings.maxDistance,
        distanceFromCamera,
      ).oneMinus()
      const closeOpacity = smoothstep(
        this.settings.hideUntilDistance,
        this.settings.minDistance,
        distanceFromCamera,
      )

      return farOpacity.mul(closeOpacity)
    })()

    // Create instanced mesh
    this.mesh = new THREE.InstancedMesh(
      geometry,
      this.material,
      this.settings.count,
    )
    this.mesh.frustumCulled = false

    this.scene.add(this.mesh)
  }

  init() {
    const { xSize, zSize, zStart, spacing } = this.grid.settings
    const xOffset = (xSize - 1) / 2

    // Calculate grid bounds in world space
    const minX = -xOffset * spacing
    const maxX = xOffset * spacing
    const minZ = (zStart - (zSize - 1)) * spacing
    const maxZ = zStart * spacing

    // Initialize particles clustered together like a school of fish
    this.computeInit = Fn(() => {
      const position = this.positions.element(instanceIndex)
      const speed = this.speeds.element(instanceIndex)

      // Generate random values for small offsets around cluster center
      const rand1 = hash(instanceIndex)
      const rand2 = hash(instanceIndex.add(1000))
      const rand3 = hash(instanceIndex.add(2000))

      // Cluster size - particles will be within this range from center
      const clusterHeight = uniform(0.2)
      const clusterWidth = uniform(5)

      // Set position based on direction
      if (
        this.settings.direction === "left-to-right" ||
        this.settings.direction === "right-to-left"
      ) {
        // Horizontal movement: cluster X around a center point, fix Y and Z
        // Start cluster at the beginning of the movement direction
        const clusterCenterX =
          this.settings.direction === "left-to-right" ? minX : maxX
        // Small random offset around cluster center
        const offsetX = rand1.mul(clusterWidth).sub(clusterWidth.mul(0.5))
        position.x = uniform(clusterCenterX).add(offsetX)
        position.y = uniform(this.settings.position.y * spacing).add(
          rand2.mul(clusterHeight).sub(clusterHeight.mul(0.5)),
        )
        position.z = uniform(
          this.settings.position.z !== undefined
            ? (zStart - this.settings.position.z) * spacing
            : 0,
        ).add(rand3.mul(clusterHeight).sub(clusterHeight.mul(0.5)))
      } else {
        // Camera direction movement: cluster Z around a center point, fix X and Y
        // Start cluster at the beginning of the movement direction
        const clusterCenterZ =
          this.settings.direction === "far-to-near" ? minZ : maxZ
        // Small random offset around cluster center
        const offsetZ = rand1.mul(clusterWidth).sub(clusterWidth.mul(0.5))
        position.x = uniform(
          this.settings.position.x !== undefined
            ? (this.settings.position.x - xOffset) * spacing
            : 0,
        ).add(rand2.mul(clusterHeight).sub(clusterHeight.mul(0.5)))
        position.y = uniform(this.settings.position.y * spacing).add(
          rand3.mul(clusterHeight).sub(clusterHeight.mul(0.5)),
        )
        position.z = uniform(clusterCenterZ).add(offsetZ)
      }

      // All particles move at the same speed (no variation)
      speed.x = uniform(1.0)
    })().compute(this.settings.count)

    // Update particles: move along specified axis, reset at bounds
    const computeUpdate = Fn(() => {
      const position = this.positions.element(instanceIndex)
      const delta = deltaTime.mul(this.settings.speed)

      const { xSize, zSize, zStart, spacing } = this.grid.settings
      const xOffset = (xSize - 1) / 2
      const minX = float(-xOffset * spacing)
      const maxX = float(xOffset * spacing)
      const minZ = float((zStart - (zSize - 1)) * spacing)
      const maxZ = float(zStart * spacing)

      // Only move particles if animation has started
      If(this.isStarted.equal(1), () => {
        // Move particle based on direction
        if (this.settings.direction === "left-to-right") {
          position.x = position.x.add(delta)

          // Reset to start when reaching end - keep relative position
          If(position.x.greaterThan(maxX), () => {
            position.x = minX
          })
        } else if (this.settings.direction === "right-to-left") {
          // Move from right to left (decreasing X)
          position.x = position.x.sub(delta)

          // Reset to start when reaching end - keep relative position
          // When particle goes past left edge (minX), reset to right edge (maxX)
          If(position.x.lessThan(minX), () => {
            position.x = maxX
          })
        } else if (this.settings.direction === "far-to-near") {
          position.z = position.z.add(delta)

          // Reset to start when reaching end - keep relative position
          If(position.z.greaterThan(maxZ), () => {
            position.z = minZ
          })
        } else if (this.settings.direction === "near-to-far") {
          position.z = position.z.sub(delta)

          // Reset to start when reaching end - keep relative position
          If(position.z.lessThan(minZ), () => {
            position.z = maxZ
          })
        }
      })
    })

    // Create the compute shader for particle updates
    this.computeParticles = computeUpdate().compute(this.settings.count)

    // Initialize particle positions on GPU
    this.renderer.computeAsync(this.computeInit)
  }

  update() {
    this.renderer.compute(this.computeParticles)
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    // Create unique folder title based on direction and position

    const positionLabel = this.settings.position.y
    const folder = this.debugFolder.addFolder({
      title: `Grid Particles (${this.name}, y:${positionLabel})`,
      expanded: false,
    })

    // Count
    folder
      .addBinding(this.settings, "count", {
        label: "Count",
        min: 1,
        max: 1000,
        step: 1,
      })
      .on("change", () => {
        // Reinitialize when count changes
        this.positions = instancedArray(this.settings.count, "vec3")
        this.speeds = instancedArray(this.settings.count, "float")
        if (this.mesh) {
          this.scene.remove(this.mesh)
          this.mesh.geometry.dispose()
          const material = this.mesh.material
          if (Array.isArray(material)) {
            material.forEach((m) => m.dispose())
          } else {
            material.dispose()
          }
        }
        this.createMeshes()
        this.init()
      })

    // Speed
    folder.addBinding(this.settings.speed, "value", {
      label: "Speed",
      min: 0,
      max: 1,
      step: 0.001,
    })

    // Color
    const colorSettings = {
      color: `#${new THREE.Color(this.settings.color.value).getHexString()}`,
    }

    folder
      .addBinding(colorSettings, "color", {
        label: "Color",
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.color.value = new THREE.Color(ev.value)
      })

    // Emissive intensity
    folder.addBinding(this.settings.emissiveIntensity, "value", {
      label: "Emissive Intensity",
      min: 0,
      max: 1,
      step: 0.01,
    })

    // Size
    folder
      .addBinding(this.settings.size, "value", {
        label: "Size",
        min: 0.0001,
        max: 0.5,
        step: 0.0001,
      })
      .on("change", () => {
        // Update geometry size
        if (this.mesh) {
          this.mesh.geometry.dispose()
          this.mesh.geometry = new THREE.PlaneGeometry(
            this.settings.size.value,
            this.settings.size.value,
            1,
            1,
          )
        }
      })

    // Distance opacity settings
    folder.addBinding(this.settings.minDistance, "value", {
      label: "Min Distance",
      min: 0,
      max: 10,
      step: 0.1,
    })

    folder.addBinding(this.settings.maxDistance, "value", {
      label: "Max Distance",
      min: 0,
      max: 20,
      step: 0.1,
    })

    folder.addBinding(this.settings.hideUntilDistance, "value", {
      label: "Hide Until Distance",
      min: 0,
      max: 5,
      step: 0.1,
    })

    // Direction
    folder
      .addBinding(this.settings, "direction", {
        label: "Direction",
        options: {
          "Left to Right": "left-to-right",
          "Right to Left": "right-to-left",
          "Far to Near": "far-to-near",
          "Near to Far": "near-to-far",
        },
      })
      .on("change", () => {
        this.init()
      })

    // Position
    const positionFolder = folder.addFolder({
      title: "Position",
      expanded: false,
    })

    if (
      this.settings.direction === "left-to-right" ||
      this.settings.direction === "right-to-left"
    ) {
      // For horizontal movement, show Z and Y
      positionFolder.addBinding(this.settings.position, "z", {
        label: "Z",
        min: 0,
        max: 39,
        step: 1,
      })
      positionFolder.addBinding(this.settings.position, "y", {
        label: "Y",
        min: 0,
        max: 9,
        step: 1,
      })
    } else {
      // For camera direction, show X and Y
      positionFolder.addBinding(this.settings.position, "x", {
        label: "X",
        min: 0,
        max: 19,
        step: 1,
      })
      positionFolder.addBinding(this.settings.position, "y", {
        label: "Y",
        min: 0,
        max: 9,
        step: 1,
      })
    }

    // Reinitialize when position changes
    positionFolder.on("change", () => {
      this.init()
    })
  }
}
